var __values = (this && this.__values) || function(o) {
    var s = typeof Symbol === "function" && Symbol.iterator, m = s && o[s], i = 0;
    if (m) return m.call(o);
    if (o && typeof o.length === "number") return {
        next: function () {
            if (o && i >= o.length) o = void 0;
            return { value: o && o[i++], done: !o };
        }
    };
    throw new TypeError(s ? "Object is not iterable." : "Symbol.iterator is not defined.");
};
/** @nocodegenimport */
import { runtime } from '@augloop/runtime-client';
/** @nocodegenimport */
import { runtimeALv1 } from '@augloop/alv1-runtime-client';
import { IdentityProvider } from '@augloop/schemas';
import { ModelDownloader } from '@augloop/model-downloader';
import { OnnxInferenceService } from '@augloop/onnx-inference-service';
import { LogCategory } from '@1js/office-online-telemetry/lib/buls/LogCategory';
import { TraceLevel } from '@1js/office-online-telemetry/lib/buls/TraceLevel';
import { ULS } from '@1js/office-online-telemetry/lib/externalLogger';
import { ExcelServerSessionInitMessage } from '@augloop/excel-session-protocol';
import { PowerPointSessionInitMessage } from '@augloop/powerpoint-session-protocol';
var getDataFieldType = function (obj) {
    var stringDataFieldType = 0;
    var booleanDataFieldType = 1;
    var int64DataFieldType = 2;
    if (typeof obj === 'string') {
        return stringDataFieldType;
    }
    else if (typeof obj === 'boolean') {
        return booleanDataFieldType;
    }
    else if (typeof obj === 'number') {
        // Only integers are supported for now
        return int64DataFieldType;
    }
    ULS.sendTraceTag(0x2224d18d /* tag_8jngn */, LogCategory.msoulscat_Wac_BrowserGeneral, TraceLevel.Warning, 'Unsupported data field type');
    return undefined;
};
var convertDataFields = function (data, prefix) {
    var e_1, _a;
    var systemMetadataDataClassification = 4;
    var dataFields = [];
    try {
        for (var _b = __values(Object.keys(data)), _c = _b.next(); !_c.done; _c = _b.next()) {
            var key = _c.value;
            if (data[key] === undefined || data[key] === null) {
                continue;
            }
            var dataField = {
                name: prefix ? "".concat(prefix, ".").concat(key) : key,
                dataType: getDataFieldType(data[key]),
                value: data[key],
                // All of our client telemetry is (must be) SystemMetadata
                classification: systemMetadataDataClassification
            };
            dataFields.push(dataField);
        }
    }
    catch (e_1_1) { e_1 = { error: e_1_1 }; }
    finally {
        try {
            if (_c && !_c.done && (_a = _b.return)) _a.call(_b);
        }
        finally { if (e_1) throw e_1.error; }
    }
    return dataFields;
};
var convertTelemetryEvent = function (tenantToken, eventName, data, contract, contractData, dataCategories, diagnosticLevel) {
    var otelEvent = {
        telemetryProperties: { ariaTenantToken: tenantToken, nexusTenantToken: -1 },
        eventName: eventName.split('_').join('.'),
        dataFields: convertDataFields(data),
        eventFlags: {
            dataCategories: dataCategories,
            diagnosticLevel: diagnosticLevel,
            samplingPolicy: 1 /* Measure */
        }
    };
    if (contract) {
        otelEvent.eventContract = {
            name: contract,
            dataFields: convertDataFields(contractData, contract.split('.').slice(-1)[0])
        };
    }
    return otelEvent;
};
// Imports to generate in ScriptSharp (not including SchemaObjects)
export { IdentityProvider, };
var areLicenseFeaturesEnabled = function (_licenseFeatures) {
    // TODO: NYI
    return Promise.resolve(false);
};
var AugLoopRuntimeManager = /** @class */ (function () {
    function AugLoopRuntimeManager() {
        var _this = this;
        this._telemetryAggregationIntervalSec = 30;
        this._eventHandlerDictionary = {};
        this.initialize = function (p) {
            // Features in different slices may each call initialize, so this is an expected case
            if (_this._runtimeInitPromise) {
                ULS.sendTraceTag(0x2224d18e /* tag_8jngo */, LogCategory.msoulscat_Wac_BrowserGeneral, TraceLevel.Info, 'Runtime already initialized');
                return;
            }
            ULS.sendTraceTag(0x2224d18f /* tag_8jngp */, LogCategory.msoulscat_Wac_BrowserGeneral, TraceLevel.Info, 'Start and initialize augloop runtime ...');
            _this.checkParametersAndThrow('initialize', p);
            var modelDownloaderPromise = _this.isFeatureEnabled('ModelDownloaderEbrake', 'None')
                .then(function (ebraked) {
                if (!ebraked) {
                    // Excluding IE due to lack of support for IDB
                    _this.modelDownloaderFromCDN = (p.appName && p.downloadBaseUrl) ? new ModelDownloader(p.appName, p.downloadBaseUrl) : undefined;
                }
                else {
                    ULS.sendTraceTag(0x2129a006 /* tag_7k0ag */, LogCategory.msoulscat_Wac_BrowserGeneral, TraceLevel.Info, 'Resource downloader not created due to feature gate');
                }
            });
            _this.inferenceServiceFactory = (p.appName && p.inferenceServiceVersion) ? OnnxInferenceService.createFactory(p.appName, p.inferenceServiceVersion) : undefined;
            _this.excelServerParameters = p.excelServer;
            _this.powerpointParameters = p.powerpoint;
            // Initialize host metadata and host callbacks
            var hostMetadata = {
                appName: p.appName,
                appPlatform: 'Web',
                appVersion: p.appVersion,
                uiLanguage: p.uiLanguage,
                releaseAudienceGroup: p.releaseAudienceGroup,
                releaseChannel: p.releaseChannel,
                releaseFork: p.releaseFork,
                sessionId: p.sessionId,
                flights: p.flights,
                privateMode: p.privateMode,
                disabledServiceGroups: p.disabledServiceGroups
            };
            var hostCallbacks = {
                onResult: _this.onResult.bind(_this),
                onAnnotationResult: _this.onAnnotationResult.bind(_this),
                sendTelemetryEvent: _this.sendTelemetryEvent.bind(_this),
                requestAuthToken: _this.requestAuthToken.bind(_this),
                isFeatureEnabled: _this.isFeatureEnabled.bind(_this),
                setSessionData: _this.setSessionData.bind(_this),
                areLicenseFeaturesEnabled: areLicenseFeaturesEnabled,
                onInitSession: _this.onInitSession.bind(_this),
                overrideSessionInitMessage: _this.overrideSessionInitMessage.bind(_this)
            };
            var runtimeInitPromise = modelDownloaderPromise
                .then(function () {
                return runtime.init(p.serviceUrl, hostMetadata, hostCallbacks, {
                    telemetryAggregationIntervalSec: _this._telemetryAggregationIntervalSec,
                    modelDownloader: _this.modelDownloaderFromCDN,
                    inferenceServiceFactory: _this.inferenceServiceFactory
                });
            })
                .then(function () {
                return runtimeALv1.init(p.serviceUrl, hostMetadata, hostCallbacks);
            });
            // Declare createSession and use it now to set _runtimeInitPromise synchronously so additional
            // calls to the AugLoopRuntimeManager are queued until _runtimeInitPromise resolves
            _this.createSession = function () {
                _this._runtimeInitPromise = runtimeInitPromise.then(function () {
                    return runtime.createSession(p.sessionCreationOptions);
                });
            };
            _this.createSession();
        };
        this.getSession = function () {
            return _this._runtimeInitPromise;
        };
        this.setULSLogger = function (p) {
            _this.checkParametersAndThrow('setULSLogger', p && p.ulsLogger);
            ULS.setLogger(p.ulsLogger);
        };
        this.setSendOTelEventCallback = function (p) {
            _this.checkParametersAndPropertyAndThrow('setSendOTelEventCallback', p && p.sendOTelEvent, _this._sendOTelEventCallback);
            _this._sendOTelEventCallback = p.sendOTelEvent;
        };
        this.setRequestAuthTokenCallback = function (p) {
            _this.checkParametersAndPropertyAndThrow('setRequestAuthTokenCallback', p && p.requestAuthTokenCallback, _this._requestAuthTokenCallback);
            _this._requestAuthTokenCallback = p.requestAuthTokenCallback;
        };
        this.setIsFeatureEnabledCallback = function (p) {
            _this.checkParametersAndPropertyAndThrow('setIsFeatureEnabledCallback', p && p.featureEnabledCallback, _this._featureEnabledCallback);
            _this._featureEnabledCallback = p.featureEnabledCallback;
        };
        /************* START STATEFUL AUGMENTATION LOOP METHODS *************/
        this.registerLocalWorkflow = function (p) {
            var functionName = 'registerLocalWorkflow';
            _this.checkParametersAndThrow(functionName, p && p.workflow);
            _this.checkRuntimeInitAndThrow(functionName);
            _this._runtimeInitPromise.then(function (session) {
                session.registerLocalWorkflow(p.workflow);
            });
        };
        this.activateAnnotation = function (p) {
            var functionName = 'activateAnnotation';
            var error = _this.checkParameters(functionName, p && p.annotationType) || _this.checkRuntimeInit(functionName);
            if (error) {
                return Promise.reject(error);
            }
            return _this._runtimeInitPromise.then(function (session) {
                var options;
                if (p.config || p.handler || p.stateUpdateHandler) {
                    options = { config: p.config, callback: p.handler, stateUpdateCallback: p.stateUpdateHandler };
                }
                return session.activateAnnotation(p.annotationType, options);
            });
        };
        this.updateAnnotationConfig = function (p) {
            var functionName = 'updateAnnotationConfig';
            var error = _this.checkParameters(functionName, p && p.token && p.config) || _this.checkRuntimeInit(functionName);
            if (error) {
                return Promise.reject(error);
            }
            return _this._runtimeInitPromise.then(function (session) {
                session.updateAnnotationConfig(p.token, p.config);
            });
        };
        this.releaseAnnotation = function (p) {
            var functionName = 'releaseAnnotation';
            var error = _this.checkParameters(functionName, p && p.token) || _this.checkRuntimeInit(functionName);
            if (error) {
                return Promise.reject(error);
            }
            return _this._runtimeInitPromise.then(function (session) {
                return session.releaseAnnotation(p.token);
            });
        };
        this.submitOperation = function (p) {
            var functionName = 'submitOperation';
            _this.checkParametersAndThrow(functionName, p && p.operation);
            _this.checkRuntimeInit(functionName);
            _this._runtimeInitPromise.then(function (session) {
                session.submitOperation(p.operation);
            });
        };
        this.setSessionDataCallback = function (p) {
            _this.checkParametersAndPropertyAndThrow('setSessionDataCallback', p && (p.sessionDataCallback || p.handler), _this._setSessionDataCallback);
            if (p.sessionDataCallback) {
                _this._setSessionDataCallback = p.sessionDataCallback;
            }
            else if (p.handler) {
                _this._setSessionDataCallback = function (sessionUrl, origin, _authToken) { return p.handler(sessionUrl, origin); };
            }
        };
        /**
         * @deprecated use activateAnnotation with a handler parameter instead
         */
        this.setAnnotationResultCallback = function (p) {
            // TODO: Throw if this function has been called already, i.e., replace this code block with:
            // this.checkParametersAndPropertyAndThrow('setAnnotationResultCallback', p && p.handler, this._annotationResultCallback);
            var functionName = 'setAnnotationResultCallback';
            _this.checkParametersAndThrow(functionName, p && p.handler);
            if (_this._annotationResultCallback) {
                var errorMessage = "".concat(functionName, " already called");
                ULS.sendTraceTag(0x200816c4 /* tag_6cb1e */, LogCategory.msoulscat_Wac_BrowserGeneral, TraceLevel.Error, errorMessage);
            }
            _this._annotationResultCallback = p.handler;
        };
        this.setAnnotationState = function (p) {
            var functionName = 'setAnnotationState';
            _this.checkParametersAndThrow(functionName, p && p.parentPath && p.id && p.value);
            _this.checkRuntimeInitAndThrow(functionName);
            _this._runtimeInitPromise.then(function (session) {
                session.setAnnotationState(p.parentPath, p.id, p.value);
            });
        };
        this.setAnnotationMetadata = function (p) {
            var functionName = 'setAnnotationMetadata';
            _this.checkParametersAndThrow(functionName, p && p.parentPath && p.id && p.value);
            _this.checkRuntimeInitAndThrow(functionName);
            _this._runtimeInitPromise.then(function (session) {
                session.setAnnotationMetadata(p.parentPath, p.id, p.value);
            });
        };
        this.setInitSessionCallback = function (p) {
            _this.checkParametersAndPropertyAndThrow('setInitSessionCallback', p && p.initSessionCallback, _this._initSessionCallback);
            _this._initSessionCallback = p.initSessionCallback;
        };
        this.setSessionInitOverrideCallback = function (p) {
            _this.checkParametersAndPropertyAndThrow('setSessionInitOverrideCallback', p && p.handler, _this._sessionInitOverrideCallback);
            _this._sessionInitOverrideCallback = p.handler;
        };
        this.forceReconnectSession = function (p) {
            var functionName = 'forceReconnectSession';
            var error = _this.checkParameters(functionName, p) || _this.checkRuntimeInit(functionName);
            if (error) {
                return Promise.reject(error);
            }
            return _this._runtimeInitPromise.then(function (session) {
                if (p.excelServerParameters) {
                    _this.excelServerParameters = p.excelServerParameters;
                }
                return session.forceReconnect();
            });
        };
        this.setSessionCloseCallback = function (p) {
            var functionName = 'setSessionCloseCallback';
            var error = _this.checkParameters(functionName, p && p.callback) || _this.checkRuntimeInit(functionName);
            if (error) {
                return Promise.reject(error);
            }
            return _this._runtimeInitPromise.then(function (session) {
                session.setSessionCloseCallback(p.callback);
            });
        };
        this.submitCustomMessage = function (p) {
            var functionName = 'submitCustomMessage';
            var error = _this.checkParameters(functionName, p && p.message) || _this.checkRuntimeInit(functionName);
            if (error) {
                return Promise.reject(error);
            }
            return _this._runtimeInitPromise.then(function (session) {
                return session.submitCustomMessage(p.message);
            });
        };
        this.closeSession = function () {
            var error = _this.checkRuntimeInit('closeSession');
            if (error) {
                return Promise.reject(error);
            }
            return _this._runtimeInitPromise.then(function (session) {
                session.close();
            });
        };
        this.startNewSession = function () {
            if (!_this.createSession) {
                var errorMessage = "startNewSession called before runtime init";
                ULS.sendTraceTag(0x2008324a /* tag_6cdjk */, LogCategory.msoulscat_Wac_BrowserGeneral, TraceLevel.Error, errorMessage);
                throw new Error(errorMessage);
            }
            _this.createSession();
        };
        /************* END OF STATEFUL AUGMENTATION LOOP METHODS *************/
        /************* START STATELESS AUGMENTATION LOOP METHODS *************/
        this.submit = function (p) {
            var functionName = 'submit';
            _this.checkParametersAndThrow(functionName, p && p.inputSchema && p.input);
            _this.checkRuntimeInitAndThrow(functionName);
            _this._runtimeInitPromise.then(function () {
                runtimeALv1.submit(p.inputSchema, p.input, p.correlationVector);
            });
        };
        this.addEventHandler = function (p) {
            _this.checkParametersAndThrow('addEventHandler', p && p.schemaName && p.handler);
            var eventHandlers = _this._eventHandlerDictionary[p.schemaName] || [];
            eventHandlers.push(p.handler);
            _this._eventHandlerDictionary[p.schemaName] = eventHandlers;
        };
        this.removeEventHandler = function (p) {
            _this.checkParametersAndThrow('removeEventHandler', p && p.schemaName && p.handler);
            var handlers = _this._eventHandlerDictionary[p.schemaName];
            if (!Array.isArray(handlers)) {
                return;
            }
            var index = handlers.indexOf(p.handler);
            if (index >= 0) {
                handlers.splice(index, 1);
            }
        };
        this.registerSimpleRemoteWorkflow = function (p) {
            var functionName = 'registerSimpleRemoteWorkflow';
            var error = _this.checkParameters(functionName, p && p.name && p.lambdaRemote) || _this.checkRuntimeInit(functionName);
            if (error) {
                return Promise.reject(error);
            }
            return _this._runtimeInitPromise.then(function () {
                return runtimeALv1.registerSimpleRemoteWorkflow(p.name, p.lambdaBefore, p.lambdaRemote, p.lambdaAfter);
            });
        };
        this.registerSimpleLocalWorkflow = function (p) {
            var functionName = 'registerSimpleLocalWorkflow';
            var error = _this.checkParameters(functionName, p && p.name && p.lambda) || _this.checkRuntimeInit(functionName);
            if (error) {
                return Promise.reject(error);
            }
            return _this._runtimeInitPromise.then(function () {
                return runtimeALv1.registerSimpleLocalWorkflow(p.name, p.lambda);
            });
        };
        this.registerReduceWorkflow = function (_p) {
            // Stubbed out ALv1 method, removal is tracked by:
            // Task 5082272: Finish removing references to ALv1 in AugLoopRuntimeManager.ts once all WAC references are removed
            return Promise.resolve();
        };
        /************* END OF STATELESS AUGMENTATION LOOP METHODS *************/
        /************* START DEPRECATED AUGMENTATION LOOP METHODS *************/
        this.registerSchemas = function (_p) {
            // Stubbed out ALv1 method, removal is tracked by:
            // Task 5082272: Finish removing references to ALv1 in AugLoopRuntimeManager.ts once all WAC references are removed
            return Promise.resolve();
        };
        /************* END OF DEPRECATED AUGMENTATION LOOP METHODS *************/
        this.onResult = function (inputSchema, input, outputSchema, output) {
            var e_2, _a;
            var eventHandlers = _this._eventHandlerDictionary[outputSchema];
            if (!eventHandlers) {
                return;
            }
            try {
                for (var eventHandlers_1 = __values(eventHandlers), eventHandlers_1_1 = eventHandlers_1.next(); !eventHandlers_1_1.done; eventHandlers_1_1 = eventHandlers_1.next()) {
                    var handler = eventHandlers_1_1.value;
                    if (handler) {
                        var result = {
                            inputSchema: inputSchema,
                            input: input,
                            outputSchema: outputSchema,
                            output: output
                        };
                        handler(result);
                    }
                }
            }
            catch (e_2_1) { e_2 = { error: e_2_1 }; }
            finally {
                try {
                    if (eventHandlers_1_1 && !eventHandlers_1_1.done && (_a = eventHandlers_1.return)) _a.call(eventHandlers_1);
                }
                finally { if (e_2) throw e_2.error; }
            }
        };
        this.onAnnotationResult = function (operation) {
            if (_this._annotationResultCallback) {
                _this._annotationResultCallback(operation);
            }
        };
        this.requestAuthToken = function (tokenRequest) {
            if (!_this._requestAuthTokenCallback) {
                return Promise.resolve({ Token: '' });
            }
            var tokenCallback = _this._requestAuthTokenCallback(tokenRequest) || Promise.resolve('');
            return tokenCallback.then(function (tokenValue) {
                var token = tokenValue || '';
                return Promise.resolve({ Token: token });
            });
        };
        this.sendTelemetryEvent = function (tenant, eventName, data, contract, contractData, _critical, dataCategories, diagnosticLevel) {
            if (_this._sendOTelEventCallback) {
                _this._sendOTelEventCallback(convertTelemetryEvent(tenant, eventName, data, contract, contractData, dataCategories, diagnosticLevel));
            }
        };
        this.isFeatureEnabled = function (feature, _scope) {
            if (!feature) {
                return Promise.resolve(false);
            }
            var preName = 'Microsoft.Office.AugLoop.';
            if (feature.indexOf(preName) === 0 && _this._featureEnabledCallback) {
                var featureName = feature.slice(preName.length);
                return Promise.resolve(_this._featureEnabledCallback(featureName));
            }
            return Promise.resolve(false);
        };
        this.setSessionData = function (sessionUrl, origin, authToken) {
            if (_this._setSessionDataCallback) {
                _this._setSessionDataCallback(sessionUrl, origin, authToken);
            }
        };
        this.overrideSessionInitMessage = function (sessionInitMessage) {
            if (_this._sessionInitOverrideCallback) {
                return _this._sessionInitOverrideCallback(sessionInitMessage);
            }
            if (sessionInitMessage.clientMetadata.appName.indexOf('Excel') === 0) {
                if (_this.excelServerParameters) {
                    return _this.overrideExcelSessionInitMessage(sessionInitMessage);
                }
            }
            if (sessionInitMessage.clientMetadata.appName.indexOf('PowerPoint') === 0) {
                if (_this.powerpointParameters) {
                    return _this.overridePowerPointSessionInitMessage(sessionInitMessage);
                }
            }
            return sessionInitMessage;
        };
        this.overrideExcelSessionInitMessage = function (sessionInitMessage) {
            var e_3, _a;
            var initMessage = {
                cluster: _this.excelServerParameters.wacCluster,
                restUrl: _this.excelServerParameters.ecsRestUrl,
                ecsId: _this.excelServerParameters.ecsSessionId,
                ecsOperationUrl: _this.excelServerParameters.ecsOperationUrl,
                workbookMetadataVersion: _this.excelServerParameters.metadataVersion,
                serverEventVersion: _this.excelServerParameters.serverEventVersion,
                permissionFlags: _this.excelServerParameters.permissionFlags,
                configurations: _this.excelServerParameters.configurations,
                collabUserListVersion: _this.excelServerParameters.userListVersion,
                collabStateId: _this.excelServerParameters.collabStateId,
                accessToken: _this.excelServerParameters.accessToken,
                accessTokenTTL: _this.excelServerParameters.accessTokenTTL,
                supportCustomMessages: _this.excelServerParameters.supportCustomMessages
            };
            try {
                for (var _b = __values(Object.keys(sessionInitMessage)), _c = _b.next(); !_c.done; _c = _b.next()) {
                    var key = _c.value;
                    initMessage[key] = sessionInitMessage[key];
                }
            }
            catch (e_3_1) { e_3 = { error: e_3_1 }; }
            finally {
                try {
                    if (_c && !_c.done && (_a = _b.return)) _a.call(_b);
                }
                finally { if (e_3) throw e_3.error; }
            }
            return new ExcelServerSessionInitMessage(initMessage);
        };
        this.overridePowerPointSessionInitMessage = function (sessionInitMessage) {
            var e_4, _a;
            var initMessage = {
                downloadUrl: _this.powerpointParameters.downloadUrl,
                getItemsEndPoint: _this.powerpointParameters.getItemsEndPoint,
                documentOpenType: _this.powerpointParameters.documentOpenType
            };
            try {
                for (var _b = __values(Object.keys(sessionInitMessage)), _c = _b.next(); !_c.done; _c = _b.next()) {
                    var key = _c.value;
                    initMessage[key] = sessionInitMessage[key];
                }
            }
            catch (e_4_1) { e_4 = { error: e_4_1 }; }
            finally {
                try {
                    if (_c && !_c.done && (_a = _b.return)) _a.call(_b);
                }
                finally { if (e_4) throw e_4.error; }
            }
            return new PowerPointSessionInitMessage(initMessage);
        };
        this.onInitSession = function () {
            if (!_this._initSessionCallback) {
                return Promise.resolve();
            }
            return _this._initSessionCallback();
        };
        /**
         * Checks params and returns an Error if it is undefined or null; otherwise returns undefined.
         */
        this.checkParameters = function (name, params) {
            if (!params) {
                var errorMessage = "".concat(name, " called with invalid parameters");
                ULS.sendTraceTag(0x2008324b /* tag_6cdjl */, LogCategory.msoulscat_Wac_BrowserGeneral, TraceLevel.Error, errorMessage);
                return new Error(errorMessage);
            }
            return undefined;
        };
        /**
         * Checks params and throws an Error if it is undefined or null.
         */
        this.checkParametersAndThrow = function (name, params) {
            var paramsError = _this.checkParameters(name, params);
            if (paramsError) {
                throw paramsError;
            }
        };
        /**
         * Checks params and throws an Error if it is undefined or null.
         * Checks property and throws an Error if it is truthy, indicating the function was already called.
         */
        this.checkParametersAndPropertyAndThrow = function (name, params, property) {
            _this.checkParametersAndThrow(name, params);
            if (property) {
                var errorMessage = "".concat(name, " already called");
                ULS.sendTraceTag(0x2008324c /* tag_6cdjm */, LogCategory.msoulscat_Wac_BrowserGeneral, TraceLevel.Error, errorMessage);
                throw new Error(errorMessage);
            }
        };
        /**
         * Returns an error if runtime init has not occurred; otherwise returns undefined.
         */
        this.checkRuntimeInit = function (name) {
            if (!_this._runtimeInitPromise) {
                var errorMessage = "".concat(name, " called before runtime init");
                ULS.sendTraceTag(0x2008324d /* tag_6cdjn */, LogCategory.msoulscat_Wac_BrowserGeneral, TraceLevel.Error, errorMessage);
                return new Error(errorMessage);
            }
            return undefined;
        };
        /**
         * Throws an error if runtime init has not occurred.
         */
        this.checkRuntimeInitAndThrow = function (name) {
            var initError = _this.checkRuntimeInit(name);
            if (initError) {
                throw initError;
            }
        };
    }
    return AugLoopRuntimeManager;
}());
export { AugLoopRuntimeManager };
//# sourceMappingURL=AugLoopRuntimeManager.js.map